import {
  getCompleteWorkTaskMutationOptions,
  getRestartWorkTaskMutationOptions,
  WorkTask as WorkTaskDto,
} from 'api-client';
import { WorkTaskStatus } from '@planitgeo/treeplotter-domain/lib/modules/work/work-task/domain/work-task.interface';
import { QueryKey } from '@tanstack/react-query';
import { queryClient as moduleQueryClient } from 'common/infrastructure/remoteState/persistQueryClient';
import {
  MutationOptions as RegistryMutationOptions,
  defaultMutationOptions,
} from 'common/infrastructure/remoteState/mutations.interface';
import loggerService, { LoggerLevel } from '../log';

type WorkTaskSnapshot = [QueryKey, WorkTaskDto[] | undefined];
export type WorkTaskMutationContext = { snapshots: WorkTaskSnapshot[] };

function createStatusMutationCallbacks(newStatus: string, errorLogMessage: string) {
  return {
    onMutate: (variables: {
      tenantId: string;
      workTaskId: string;
      data: { userId: string };
    }): WorkTaskMutationContext => {
      const findWorkTasksSnapshots = moduleQueryClient.getQueriesData<WorkTaskDto[]>({
        queryKey: ['findWorkTasks'],
      });
      const findWorkTasksByAssetIdsSnapshots = moduleQueryClient.getQueriesData<WorkTaskDto[]>({
        queryKey: ['findWorkTasksByAssetIds'],
      });
      const snapshots: WorkTaskSnapshot[] = [
        ...findWorkTasksSnapshots,
        ...findWorkTasksByAssetIdsSnapshots,
      ];

      for (const [key] of snapshots) {
        moduleQueryClient.setQueryData<WorkTaskDto[]>(key, (old: WorkTaskDto[] | undefined) => {
          if (!old) return undefined;
          return old.map((task) =>
            task.id === variables.workTaskId ? { ...task, status: newStatus } : task
          );
        });
      }

      return { snapshots };
    },

    onError: (
      error: unknown,
      variables: {
        tenantId: string;
        workTaskId: string;
        data: { userId: string };
      },
      context: WorkTaskMutationContext | undefined
    ): void => {
      if (context?.snapshots) {
        for (const [key, data] of context.snapshots) {
          moduleQueryClient.setQueryData(key, data);
        }
      }
      loggerService.log(LoggerLevel.ERROR, errorLogMessage, {
        error: error instanceof Error ? error.message : String(error),
        tenantId: variables.tenantId,
        workTaskId: variables.workTaskId,
      });
    },

    onSettled: (): void => {
      // Intentionally no invalidateQueries here: the optimistic update already
      // wrote the correct status into the cache. Immediate invalidation would
      // trigger a refetch that moves the task between tabs before the user has
      // a chance to see or undo the change. React Query will refetch naturally
      // on window focus / remount to eventually sync server state.
    },
  };
}

const completeCallbacks = createStatusMutationCallbacks(
  WorkTaskStatus.COMPLETED,
  'Complete work task error'
);
const restartCallbacks = createStatusMutationCallbacks(
  WorkTaskStatus.OPEN,
  'Restart work task error'
);

/**
 * Shared onMutate for complete-work-task mutations.
 * Snapshots the cache and applies the optimistic status update.
 * Compose this in UI layers that add extra side-effects on mutate.
 */
export const onCompleteWorkTaskMutate = completeCallbacks.onMutate;

/**
 * Shared onError for complete-work-task mutations.
 * Rolls back the optimistic cache update and logs a structured error.
 * Compose this in UI layers that need to show additional feedback (e.g. snackbar).
 */
export const onCompleteWorkTaskError = completeCallbacks.onError;

/**
 * Shared onMutate for restart-work-task mutations.
 * Snapshots the cache and applies the optimistic status update.
 * Compose this in UI layers that add extra side-effects on mutate.
 */
export const onRestartWorkTaskMutate = restartCallbacks.onMutate;

/**
 * Shared onError for restart-work-task mutations.
 * Rolls back the optimistic cache update and logs a structured error.
 * Compose this in UI layers that need to show additional feedback (e.g. snackbar).
 */
export const onRestartWorkTaskError = restartCallbacks.onError;

export function getCompleteWorkTaskMutationConfig(): RegistryMutationOptions {
  return getCompleteWorkTaskMutationOptions({
    mutation: {
      ...defaultMutationOptions,
      ...completeCallbacks,
    },
  }) as RegistryMutationOptions;
}

export function getRestartWorkTaskMutationConfig(): RegistryMutationOptions {
  return getRestartWorkTaskMutationOptions({
    mutation: {
      ...defaultMutationOptions,
      ...restartCallbacks,
    },
  }) as RegistryMutationOptions;
}
